import React, { Fragment } from 'react';
import { styled } from '@mui/material/styles';
import { withTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { connect } from 'react-redux';
import Form from 'components/form';
import Input from 'components/form/TextInput';
import Select from 'components/form/Select';
import PhoneInput from 'components/form/PhoneInput';
import Button from 'components/Button';
import Spin from 'components/Spin';
import { toDbPhone, validatePhone } from 'utils';
import { getDCCData } from 'store/modules/dcc/actions';
import { createContact, addContactToGroup } from 'services/contacts.service';
import toast from 'utils/toast';
import { WEBSITE } from 'constants/config';
import { STATES } from 'constants/states';
import MetaTags from '../components/MetaTags';

const FormItem = Form.FormItem;

const Container = styled('div')`
  padding: 2em;
  background: #fff;
  width: 100%;
  max-width: 600px;
  margin: auto;
  border-top: 8px solid ${(p) => p.color};
  min-height: 100vh;
  form {
    margin-bottom: 30px;
  }
  .logo {
    max-width: 200px;
    max-height: 100px;
    height: auto;
    margin: auto;
    display: block;
    margin-bottom: 20px;
  }
  h2 {
    margin-top: 1em;
    margin-bottom: 2em;
    position: relative;
    &:after {
      position: absolute;
      content: ' ';
      background: ${(p) => p.color};
      height: 4px;
      width: 4em;
      left: 0;
      bottom: -1em;
    }
  }

  .MuiButton-root {
    background-color: ${(p) => p.color};
  }
`;

class BasicForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      done: false,
      isNew: false,
    };
  }

  componentDidMount() {
    if (!this.props.dcc) {
      this.props.getDCCData(
        this.props.match.params.groupId,
        this.props.match.params.contactId,
      );
    }
  }

  handleSubmit = (e) => {
    e.preventDefault();
    const {
      dcc: { group_name, account_id, group_comment_title },
      form,
      match: { params },
      t,
    } = this.props;
    return form
      .validateFields()
      .then(
        ({
          firstName,
          lastName,
          email,
          mobile,
          notes,
          address,
          city,
          state,
          zip,
        }) => {
          if (!(firstName || lastName || email || mobile)) {
            return toast.error(
              t(
                'empty-form-error',
                'Please complete at least one field before submitting the form.',
              ),
            );
          } else if (!email && !mobile) {
            return toast.error(
              t(
                'empty-contact-error',
                'Please provide at least one contact information before submitting the form.',
              ),
            );
          }

          let comments;
          if (notes) {
            comments = `
          ----------
          Group: ${group_name}
          ${group_comment_title}: ${notes}
          ----------
          `;
          }

          const payload = {
            account_id: account_id,
            contact_id: !this.state.isNew && params.contactId,
            contact_first_name: firstName,
            contact_last_name: lastName,
            contact_email: email,
            primary_phone: toDbPhone(mobile),
            contact_address1: address,
            contact_city: city,
            contact_state: state,
            contact_zip: zip,
            contact_comments: comments,
            contact_source: 'Connect Card',
            contact_source_id: params.groupId,
          };

          return createContact(payload)
            .then((contact) => {
              return addContactToGroup(
                contact.hash,
                params.groupId,
                1,
                1,
                'DCC',
              );
            })
            .then(
              () => {
                this.setState({ done: true });
              },
              (e) => {
                toast.error(e.message);
              },
            );
        },
      );
  };

  onAddAnother = () => {
    this.props.form.resetFields();
    this.setState({ done: false, isNew: true });
  };

  renderThankYou = () => {
    const { t } = this.props;
    return (
      <div>
        <Box mb={3}>
          <Trans ns="dcc" i18nKey="confirmation">
            <Typography variant="h2">Thanks!</Typography>
            <Typography>We have received your information.</Typography>
          </Trans>
        </Box>
        <Button size="large" onClick={this.onAddAnother}>
          {t('add-another-person', 'Add Another Person')}
        </Button>
      </div>
    );
  };

  renderAddressFields = () => {
    const {
      dcc,
      form: { getFieldDecorator },
      t,
    } = this.props;
    const { isNew } = this.state;
    return (
      <Fragment>
        <FormItem>
          {getFieldDecorator('address', {
            initialValue: !isNew ? dcc.contact_address1 : '',
          })(<Input fullWidth placeholder={t('common:address')} size="lg" />)}
        </FormItem>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <FormItem>
              {getFieldDecorator('city', {
                initialValue: !isNew ? dcc.contact_city : '',
              })(<Input fullWidth placeholder={t('common:city')} size="lg" />)}
            </FormItem>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormItem>
              {getFieldDecorator('state', {
                initialValue: dcc.contact_state || dcc.account_state,
              })(
                <Select
                  fullWidth
                  transformValue
                  options={STATES[dcc.account_country] || STATES.US}
                  placeholder={t('common:state')}
                  size="lg"
                />,
              )}
            </FormItem>
          </Grid>
        </Grid>
        <FormItem>
          {getFieldDecorator('zip', {
            initialValue: !isNew ? dcc.contact_zip : '',
          })(<Input fullWidth placeholder={t('common:zip')} size="lg" />)}
        </FormItem>
      </Fragment>
    );
  };

  render() {
    const {
      isExtended,
      form: { getFieldDecorator },
      dcc,
      loading,
      location,
      t,
    } = this.props;
    const { done, isNew } = this.state;

    const color = '#' + (dcc && dcc.account_color) || '1E8AFB';

    if (loading)
      return (
        <Container color={color}>
          <Spin spinning />
        </Container>
      );
    else if (!dcc) {
      return (
        <Container>
          <p>
            {t(
              'invalid-link-error',
              'Content not found. Please make sure you have followed a valid link.',
            )}
          </p>
        </Container>
      );
    }

    return (
      <Container color={color}>
        {dcc.account_logo_url &&
        dcc.account_logo_url !==
          'https://textinchurchattachments.s3.amazonaws.com/' ? (
          <img className="logo" src={dcc.account_logo_url} />
        ) : (
          <Typography variant="h2">
            <Trans ns="dcc" i18nKey="header">
              Connect With {{ church_name: dcc.account_name }}!
            </Trans>
          </Typography>
        )}

        {done ? (
          this.renderThankYou()
        ) : (
          <Fragment>
            <MetaTags group={dcc} t={t} location={location} />
            <Typography gutterBottom>
              {t(
                'prompt',
                'Please enter your information to stay in contact with us.',
              )}
            </Typography>
            <Form>
              <FormItem>
                {getFieldDecorator('firstName', {
                  initialValue: !isNew ? dcc.contact_first_name : '',
                })(
                  <Input
                    fullWidth
                    placeholder={t('common:firstName')}
                    size="lg"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('lastName', {
                  initialValue: !isNew ? dcc.contact_last_name : '',
                })(
                  <Input
                    fullWidth
                    placeholder={t('common:lastName')}
                    size="lg"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('email', {
                  initialValue: !isNew ? dcc.contact_email : '',
                  rules: [
                    {
                      required: false,
                      message: t('common:invalid-email'),
                      type: 'email',
                    },
                  ],
                })(
                  <Input
                    fullWidth
                    type="email"
                    placeholder={t('common:email')}
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('mobile', {
                  initialValue: !isNew ? dcc.primary_phone : '',
                  rules: [
                    { required: false, message: t('common:invalid-phone') },
                    { validator: validatePhone },
                  ],
                })(
                  <PhoneInput
                    fullWidth
                    country={dcc.account_country}
                    placeholder={t('common:mobileNumber')}
                  />,
                )}
              </FormItem>
              {isExtended && this.renderAddressFields()}
              {dcc.group_comment_title && (
                <FormItem>
                  {getFieldDecorator('notes')(
                    <Input fullWidth placeholder={dcc.group_comment_title} />,
                  )}
                </FormItem>
              )}
              <Box textAlign="center" mt={2}>
                <Button size="large" onClick={this.handleSubmit}>
                  {t('common:submit', 'Submit')}
                </Button>
              </Box>
            </Form>
            <div>
              <Typography variant="caption">
                <Trans ns="dcc" i18nKey="footer">
                  We will not share your contact info.
                  <br />
                  By clicking submit you agree to receive communications from{' '}
                  {{ church_name: dcc.account_name }} (frequency varies).
                  Message and data rates may apply. Text STOP at any time to
                  cancel or text HELP for help.
                  <br />
                  <Link target="_blank" href={`${WEBSITE}/terms-of-use`}>
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link target="_blank" href={`${WEBSITE}/privacy-policy`}>
                    Privacy Policy
                  </Link>
                </Trans>
              </Typography>
            </div>
          </Fragment>
        )}
      </Container>
    );
  }
}

export default connect((state) => state.dcc, { getDCCData })(
  Form.create()(withTranslation('dcc')(BasicForm)),
);
